import {
  CountryEnum,
  CryptoCurrencyEnum,
  ProcessTypeEnum,
  type CreatingApplicationCommand,
  type CurrencyTypeEnum,
} from '@acme/api';
import type { FieldConfig, FormConfig, SelectOption } from '@acme/foundation';

export type ApplicationFormationSubmitKind = 'draft' | 'review';

export type ApplicationFormationFormCtx =
  Partial<CreatingApplicationCommand> & {
    __submitKind?: ApplicationFormationSubmitKind;
    counterpartyAddress?: string | null;
  };

type DictionariesOptionsType = {
  docTypesOptions: SelectOption[];
  currencyOptions: SelectOption[];
  countryOptions: SelectOption[];
  cryptoCurrencyOptions: SelectOption[];
};

type Bag = {
  values: Record<string, unknown>;
  ctx: ApplicationFormationFormCtx;
};

const getApplicationType = ({ values, ctx }: Bag) =>
  Number(values.applicationType ?? ctx.applicationType);

const isReview = ({ values, ctx }: Bag) =>
  (values.__submitKind ?? ctx.__submitKind) === 'review';

const isOutgoing = (bag: Bag) =>
  getApplicationType(bag) === ProcessTypeEnum.Outgoing;

const isIncoming = (bag: Bag) =>
  getApplicationType(bag) === ProcessTypeEnum.Incoming;

const isCryptoPayment = ({ values, ctx }: Bag) =>
  !!(values.isCryptoPayment ?? ctx.isCryptoPayment);

const shouldIncludeBankDetails = (ctx: ApplicationFormationFormCtx) =>
  ctx.applicationType === ProcessTypeEnum.Outgoing && !ctx.isCryptoPayment;

const needsCryptoCurrency = (bag: Bag) =>
  isIncoming(bag) || (isOutgoing(bag) && isCryptoPayment(bag));

// required-логика для подсветки звёздочкой (как в дизайне):
// показываем required по “финальной” отправке, даже если при черновике поле можно не заполнять
const requiredAlways = { when: () => true, set: { required: true } } as const;
const requiredIf = (when: (bag: Bag) => boolean) =>
  ({ when, set: { required: true } }) as const;

const toNullableString = (v: unknown): string | null => {
  const s = String(v ?? '').trim();
  return s ? s : null;
};

const toOptionalNumber = (v: unknown): number | undefined => {
  if (v === '' || v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

export const createapplicationformationformconfig = (
  includeUnchangedFields: boolean = true,
  dictionariesOptions: DictionariesOptionsType
): FormConfig<ApplicationFormationFormCtx> => {
  const controls: FieldConfig<ApplicationFormationFormCtx>[] = [
    {
      id: 'applicationType',
      label: 'Тип заявки',
      type: 'select',
      read: (ctx) => String(ctx?.applicationType ?? ''),
      write: {
        path: 'applicationType',
        transform: (v) => Number(v) as ProcessTypeEnum,
      },
      validation: (value) => {
        if (!value) return 'Тип заявки обязателен';
        return null;
      },
      props: { placeholder: 'Выберите тип заявки' },
      options: { source: dictionariesOptions.docTypesOptions },
      rules: [requiredAlways],
    },
    {
      id: 'name',
      label: 'Название',
      type: 'text',
      read: (ctx) => ctx?.name ?? '',
      write: { path: 'name', transform: toNullableString },
      validation: (value) =>
        !String(value ?? '').trim() ? 'Название обязательно' : null,
      props: { placeholder: '' },
      rules: [requiredAlways],
    },
    {
      id: 'counterparty',
      label: 'Контрагент',
      type: 'text',
      read: (ctx) => ctx?.counterparty ?? '',
      write: { path: 'counterparty', transform: toNullableString },
      validation: (value, { values, ctx, showValidation }) => {
        if (!isReview({ values, ctx }) && !showValidation) return null;
        if (!String(value ?? '').trim()) return 'Контрагент обязателен';
        return null;
      },
      props: { placeholder: 'Напишите или вставьте контрагента' },
      rules: [requiredAlways],
    },
    {
      id: 'counterpartyAddress',
      label: 'Адрес контрагента',
      type: 'text',
      read: (ctx) => ctx?.counterpartyAddress ?? '',
      write: { path: 'counterpartyAddress', transform: toNullableString },
      validation: (value, { values, ctx, showValidation }) => {
        if (!isReview({ values, ctx }) && !showValidation) return null;
        if (!String(value ?? '').trim()) return 'Адрес контрагента обязателен';
        return null;
      },
      props: { placeholder: 'Напишите или вставьте адрес контрагента' },
      rules: [requiredAlways],
    },
    {
      id: 'dealSubject',
      label: 'Предмет сделки',
      type: 'text',
      read: (ctx) => ctx?.dealSubject ?? '',
      write: { path: 'dealSubject', transform: toNullableString },
      validation: (value, { values, ctx, showValidation }) => {
        if (!isReview({ values, ctx }) && !showValidation) return null;
        if (!String(value ?? '').trim()) return 'Предмет сделки обязателен';
        return null;
      },
      props: { placeholder: '' },
      rules: [requiredAlways],
    },
    {
      id: 'dealAmount',
      label: 'Сумма сделки',
      type: 'number',
      read: (ctx) => ctx?.dealAmount ?? '',
      write: { path: 'dealAmount', transform: (v) => toOptionalNumber(v) ?? 0 },
      validation: (value, { values, ctx, showValidation }) => {
        if (!isReview({ values, ctx }) && !showValidation) return null;
        const n = toOptionalNumber(value);
        if (!n || n <= 0) return 'Сумма сделки обязательна';
        return null;
      },
      props: { placeholder: '' },
      rules: [requiredAlways],
    },
    {
      id: 'transferCurrency',
      label: 'Валюта перевода',
      type: 'select',
      read: (ctx) => String(ctx?.transferCurrency ?? ''),
      write: {
        path: 'transferCurrency',
        transform: (v) => Number(v) as CurrencyTypeEnum,
      },
      validation: (value, bag) => {
        if (!isReview(bag) && !bag.showValidation) return null;
        if (!value) return 'Валюта перевода обязательна';
        return null;
      },
      props: { placeholder: 'Выберите валюту' },
      options: { source: dictionariesOptions.currencyOptions },
      rules: [requiredIf(isReview)],
    },
    {
      id: 'isCryptoPayment',
      label: 'Платеж в цифровой валюте',
      type: 'checkbox',
      read: (ctx) =>
        ctx?.applicationType === ProcessTypeEnum.Incoming
          ? true
          : !!ctx?.isCryptoPayment,
      write: {
        path: 'isCryptoPayment',
        transform: (v, ctx) =>
          ctx?.applicationType === ProcessTypeEnum.Incoming ? true : v,
      },
      props: { className: 'col-span-2' },
      rules: [
        {
          when: (bag) => isIncoming(bag),
          set: { hidden: true },
        },
      ],
    },

    // Bank details (только исходящая и НЕ цифровая валюта)
    {
      id: 'bankDetails.destinationCountry',
      label: 'Страна назначения',
      type: 'select',
      read: (ctx) => String(ctx?.bankDetails?.destinationCountry ?? ''),
      write: {
        path: 'bankDetails.destinationCountry',
        transform: (v, ctx) =>
          shouldIncludeBankDetails(
            ctx as unknown as ApplicationFormationFormCtx
          )
            ? (Number(v) as CountryEnum)
            : undefined,
      },
      validation: (value, bag) => {
        if (!isReview(bag) && !bag.showValidation) return null;
        if (!isOutgoing(bag) || isCryptoPayment(bag)) return null;
        if (!value) return 'Страна назначения обязательна';
        return null;
      },
      props: { placeholder: 'Выберите страну' },
      options: { source: dictionariesOptions.countryOptions },
      rules: [
        {
          when: (bag) => !isOutgoing(bag) || isCryptoPayment(bag),
          set: { hidden: true },
        },
        requiredIf((bag) => isOutgoing(bag) && !isCryptoPayment(bag)),
      ],
    },
    {
      id: 'bankDetails.bankName',
      label: 'Название банка',
      type: 'text',
      read: (ctx) => ctx?.bankDetails?.bankName ?? '',
      write: {
        path: 'bankDetails.bankName',
        transform: (v, ctx) =>
          shouldIncludeBankDetails(
            ctx as unknown as ApplicationFormationFormCtx
          )
            ? toNullableString(v)
            : undefined,
      },
      validation: (value, bag) => {
        if (!isReview(bag) && !bag.showValidation) return null;
        if (!isOutgoing(bag) || isCryptoPayment(bag)) return null;
        if (!String(value ?? '').trim()) return 'Название банка обязательно';
        return null;
      },
      props: { placeholder: 'Напишите или вставьте название банка' },
      rules: [
        {
          when: (bag) => !isOutgoing(bag) || isCryptoPayment(bag),
          set: { hidden: true },
        },
        requiredIf((bag) => isOutgoing(bag) && !isCryptoPayment(bag)),
      ],
    },
    {
      id: 'bankDetails.bankAddress',
      label: 'Адрес банка',
      type: 'text',
      read: (ctx) => ctx?.bankDetails?.bankAddress ?? '',
      write: {
        path: 'bankDetails.bankAddress',
        transform: (v, ctx) =>
          shouldIncludeBankDetails(
            ctx as unknown as ApplicationFormationFormCtx
          )
            ? toNullableString(v)
            : undefined,
      },
      validation: (value, bag) => {
        if (!isReview(bag) && !bag.showValidation) return null;
        if (!isOutgoing(bag) || isCryptoPayment(bag)) return null;
        if (!String(value ?? '').trim()) return 'Адрес банка обязателен';
        return null;
      },
      props: {
        placeholder: 'Напишите или вставьте адрес банка',
        className: 'col-span-2',
      },
      rules: [
        {
          when: (bag) => !isOutgoing(bag) || isCryptoPayment(bag),
          set: { hidden: true },
        },
        requiredIf((bag) => isOutgoing(bag) && !isCryptoPayment(bag)),
      ],
    },
    {
      id: 'bankDetails.iban',
      label: 'IBAN',
      type: 'text',
      read: (ctx) => ctx?.bankDetails?.iban ?? '',
      write: {
        path: 'bankDetails.iban',
        transform: (v, ctx) =>
          shouldIncludeBankDetails(
            ctx as unknown as ApplicationFormationFormCtx
          )
            ? toNullableString(v)
            : undefined,
      },
      validation: (value, bag) => {
        if (!isReview(bag) && !bag.showValidation) return null;
        if (!isOutgoing(bag) || isCryptoPayment(bag)) return null;
        if (!String(value ?? '').trim()) return 'IBAN обязателен';
        return null;
      },
      props: { placeholder: 'Введите или вставьте номер' },
      rules: [
        {
          when: (bag) => !isOutgoing(bag) || isCryptoPayment(bag),
          set: { hidden: true },
        },
        requiredIf((bag) => isOutgoing(bag) && !isCryptoPayment(bag)),
      ],
    },
    {
      id: 'bankDetails.swift',
      label: 'SWIFT/BIC',
      type: 'text',
      read: (ctx) => ctx?.bankDetails?.swift ?? '',
      write: {
        path: 'bankDetails.swift',
        transform: (v, ctx) =>
          shouldIncludeBankDetails(
            ctx as unknown as ApplicationFormationFormCtx
          )
            ? toNullableString(v)
            : undefined,
      },
      validation: (value, bag) => {
        if (!isReview(bag) && !bag.showValidation) return null;
        if (!isOutgoing(bag) || isCryptoPayment(bag)) return null;
        if (!String(value ?? '').trim()) return 'SWIFT/BIC обязателен';
        return null;
      },
      props: { placeholder: 'Введите или вставьте код' },
      rules: [
        {
          when: (bag) => !isOutgoing(bag) || isCryptoPayment(bag),
          set: { hidden: true },
        },
        requiredIf((bag) => isOutgoing(bag) && !isCryptoPayment(bag)),
      ],
    },

    // Crypto details (исходящая + цифровая валюта)
    {
      id: 'cryptoDetails.walletAddress',
      label: 'Адрес криптокошелька',
      type: 'text',
      read: (ctx) => ctx?.cryptoDetails?.walletAddress ?? '',
      write: {
        path: 'cryptoDetails.walletAddress',
        transform: toNullableString,
      },
      validation: (value, bag) => {
        if (!isReview(bag) && !bag.showValidation) return null;
        if (!isOutgoing(bag) || !isCryptoPayment(bag)) return null;
        if (!String(value ?? '').trim())
          return 'Адрес криптокошелька обязателен';
        return null;
      },
      props: {
        placeholder: 'Напишите или вставьте адрес криптокошелька',
        className: 'col-span-2',
      },
      rules: [
        {
          when: (bag) => !isOutgoing(bag) || !isCryptoPayment(bag),
          set: { hidden: true },
        },
        requiredIf((bag) => isOutgoing(bag) && isCryptoPayment(bag)),
      ],
    },
    // Crypto amount (входящая ИЛИ исходящая+цифровая)
    {
      id: 'cryptoDetails.cryptoAmount',
      label: 'Объем цифровой валюты',
      type: 'number',
      read: (ctx) => ctx?.cryptoDetails?.cryptoAmount ?? '',
      write: {
        path: 'cryptoDetails.cryptoAmount',
        transform: toOptionalNumber,
      },
      validation: (value, bag) => {
        if (!isReview(bag) && !bag.showValidation) return null;
        if (!needsCryptoCurrency(bag)) return null;
        const n = toOptionalNumber(value);
        if (!n || n <= 0) return 'Объем цифровой валюты обязателен';
        return null;
      },
      props: { placeholder: '' },
      rules: [
        {
          when: (bag) => !needsCryptoCurrency(bag),
          set: { hidden: true },
        },
        requiredIf(needsCryptoCurrency),
      ],
    },
    {
      id: 'cryptoDetails.cryptocurrency',
      label: 'Наименование цифровой валюты',
      type: 'select',
      read: (ctx) => String(ctx?.cryptoDetails?.cryptocurrency ?? ''),
      write: {
        path: 'cryptoDetails.cryptocurrency',
        transform: (v) => Number(v) as CryptoCurrencyEnum,
      },
      validation: (value, bag) => {
        if (!isReview(bag) && !bag.showValidation) return null;
        if (!needsCryptoCurrency(bag)) return null;
        if (!value) return 'Цифровая валюта обязательна';
        return null;
      },
      props: { placeholder: 'Выберите валюту' },
      options: { source: dictionariesOptions.cryptoCurrencyOptions },
      rules: [
        {
          when: (bag) => !needsCryptoCurrency(bag),
          set: { hidden: true },
        },
        requiredIf(needsCryptoCurrency),
      ],
    },
  ];

  return {
    id: 'application-formation-form',
    includeUnchangedFields,
    controls,
  };
};
